import React, { useState, useEffect } from 'react';
import { Fixture } from '../../models/Fixture';
import { FixturesContainer, PageTitle, FilterButton, PaginationSection, ShowAllButton, LoadingState, ErrorState, NoFixturesMessage } from './Fixtures.Styles';
import FixturesCard from '../../components/FixturesCard';
import GameFilter from '../../components/GameFilter';

const Fixtures: React.FC = () => {
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter] = useState<'all' | 'home' | 'away'>('all');
  const [showAll, setShowAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const fixturesPerPage = 6;

  useEffect(() => {
    const fetchFixtures = async () => {
      try {
        const response = await fetch('/content/fixtures.json?v=' + new Date().getTime());
        if (!response.ok) {
          throw new Error('Failed to fetch fixtures');
        }
        const data = await response.json();

        // Filter out past fixtures
        const now = new Date();
        const futureFixtures = data.fixtures.filter((fixture: Fixture) => {
          const gameDateTime = new Date(`${fixture.date}T${fixture.time}`);
          return gameDateTime > now;
        });

        // Sort fixtures by date
        const sortedFixtures = futureFixtures.sort((a: Fixture, b: Fixture) => {
          const dateA = new Date(`${a.date}T${a.time}`);
          const dateB = new Date(`${b.date}T${b.time}`);
          return dateA.getTime() - dateB.getTime();
        });

        setFixtures(sortedFixtures);
        setLoading(false);
      } catch (err) {
        setError('Unable to load fixtures. Please try again later.');
        setLoading(false);
      }
    };

    fetchFixtures();
  }, []);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const filteredFixtures = fixtures.filter(fixture => {
    if (filter === 'home') return fixture.homeTeam === 'Sheffield Ice Tigers';
    if (filter === 'away') return fixture.awayTeam === 'Sheffield Ice Tigers';
    return true;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredFixtures.length / fixturesPerPage);
  const displayedFixtures = showAll
    ? filteredFixtures
    : filteredFixtures.slice((currentPage - 1) * fixturesPerPage, currentPage * fixturesPerPage);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const toggleShowAll = () => {
    setShowAll(prev => !prev);
    if (!showAll) {
      setCurrentPage(1);
    }
  };

  if (loading) {
    return (
      <FixturesContainer>
        <LoadingState>Loading fixtures...</LoadingState>
      </FixturesContainer>
    );
  }

  if (error) {
    return (
      <FixturesContainer>
        <ErrorState>{error}</ErrorState>
      </FixturesContainer>
    );
  }

  return (
    <FixturesContainer>
      <PageTitle>Upcoming Fixtures</PageTitle>
      <GameFilter />

      {filteredFixtures.length === 0 ? (
        <NoFixturesMessage>
          No {filter === 'all' ? '' : filter} fixtures found
        </NoFixturesMessage>
      ) : (
        <>
          {displayedFixtures.map((fixture, index) => (
            <FixturesCard fixture={fixture} />
          ))}

          {filteredFixtures.length > fixturesPerPage && (
            <>
              {!showAll && (
                <PaginationSection>
                  <FilterButton onClick={handlePreviousPage} disabled={currentPage === 1}>
                    Previous
                  </FilterButton>
                  <span className="page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <FilterButton onClick={handleNextPage} disabled={currentPage === totalPages}>
                    Next
                  </FilterButton>
                </PaginationSection>
              )}

              <ShowAllButton onClick={toggleShowAll}>
                {showAll ? 'Show Less' : 'Show All Fixtures'}
              </ShowAllButton>
            </>
          )}
        </>
      )}

      <p style={{ textAlign: 'center', color: 'var(--gray)', marginTop: '2rem' }}>
        * Fixture times are subject to change
      </p>
    </FixturesContainer>
  );
};

export default Fixtures; 